import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SuggestionUser } from '../types/search';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../integrations/supabase/client';

// Helper para fetch autenticado
const fetchWithAuth = async (url: string, body: any) => {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || ''
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body)
  });
};

export const useSearchSuggestions = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Função para buscar sugestões
  const getSuggestions = async (userId: string, limit: number = 3) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || ''
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get_suggestions`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ userId, limit })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      console.error('Get suggestions error:', error);
      throw error;
    }
  };

  // Função para limpar cache de sugestões
  const clearSuggestionsCache = (userId: string) => {
    queryClient.removeQueries({ queryKey: ['searchSuggestions', userId] });
  };

  const { data: suggestions = [], isLoading } = useQuery({
    queryKey: ['searchSuggestions', user?.id],
    queryFn: async () => {
      if (!user?.id) {
        return [];
      }
      
      const result = await getSuggestions(user.id, 3);
      
      if (!result?.suggestions) {
        return [];
      }
      
      const mapped = result.suggestions.map(suggestion => ({
        id: suggestion.id,
        full_name: suggestion.name, // Mapeando 'name' para 'full_name'
        username: suggestion.username,
        avatar_url: suggestion.avatar_url,
        belt_rank: suggestion.belt_rank,
        academy_name: suggestion.academy_name,
        affinityScore: suggestion.affinityScore
      }));
      
      return mapped;
    },
    enabled: !!user?.id,
    staleTime: 0, // Sempre considerar stale para forçar refetch
  });

  const connectMutation = useMutation({
    mutationFn: async (recipientId: string) => {
      if (!user?.id) throw new Error('User not authenticated');
      
      const response = await fetchWithAuth(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send_connection_request`, {
        requesterId: user.id,
        recipientId: recipientId
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to send connection request');
      }

      const result = await response.json();
      return result;
    },
    onSuccess: (data, recipientId) => {
      queryClient.setQueryData(['searchSuggestions', user?.id], (oldData: any) => {
        if (!Array.isArray(oldData)) {
          return oldData;
        }
        
        const filtered = oldData.filter((suggestion: any) => {
          const shouldKeep = suggestion.id !== recipientId;
          return shouldKeep;
        });
        
        return filtered;
      });

      // Limpar o cache específico das sugestões no useSearchApi
      if (user?.id) {
        clearSuggestionsCache(user.id);
      }

      // Invalidar o cache para buscar novas sugestões na próxima vez
      queryClient.invalidateQueries({ queryKey: ['searchSuggestions', user?.id] });
    },
    onError: (error, recipientId) => {

    }
  });

  const handleConnect = (recipientId: string) => {
    connectMutation.mutate(recipientId);
  };

  const isConnecting = (recipientId: string) => {
    return connectMutation.isPending && connectMutation.variables === recipientId;
  };

  return {
    suggestions,
    isLoading,
    handleConnect,
    isConnecting
  };
}; 